import type { ProviderDetail } from '@katoa/schema-provider'
import { Hospital, MapPin, Phone, Shield, Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import { data, href, isRouteErrorResponse, useLoaderData, useNavigate } from 'react-router'
import { fetchProviderDetail } from '../api/provider-detail.server'
import { resolveZipLocation } from '../api/zip-location.server'
import { resultsPathFromReferrer } from '../back-navigation'
import { Avatar } from '../components/avatar'
import { DollarRating } from '../components/dollar-rating'
import { SiteHeader } from '../components/header'
import { formatPhoneNumber, formatTelHref } from '../components/phone'
import { BackHomeLink, StatusScreen, TryAgainButton } from '../components/status-screen'
import { cardPath } from '../og/card-path'
import { pluralize } from '../plural'
import { GENERIC_PROVIDER_DESCRIPTION, providerDescription } from '../provider-description'
import {
  formatAddressLine,
  locationKey,
  type ProviderLocation,
  primaryPhone,
  renderableLocations,
} from '../provider-locations'
import { formatProviderName, formatProviderNameLead } from '../provider-name'
import { fitTitle, pageMeta, TITLE_SUFFIX } from '../seo'
import { SITE_NAME } from '../site'
import { formatCityZipSlug, formatLocationLabel, formatProviderSlug, parseProviderNpi } from '../slug'
import { specialtyForDisplayName } from '../specialties'
import { type BreadcrumbRung, breadcrumbList, physician } from '../structured-data'
import { isUsRegionCode } from '../us-regions'
import type { Route } from './+types/provider'

// Forward any loader-set headers onto the outgoing response. Cache-Control is
// set only by the loader's success return, so a thrown 404/500 never picks it
// up and stays uncached at the edge — this route exports its own ErrorBoundary,
// and React Router runs the boundary route's `headers` fn, so a flat headers
// export would stamp s-maxage onto error responses. Mirrors results.tsx.
export const headers: Route.HeadersFunction = ({ loaderHeaders }) => loaderHeaders

// Shared-cache the provider page (and its `.data` sibling) at the vit.al edge.
// The honor-origin cache policy in sst.config.ts has defaultTtl 0, so without
// this header every request reaches the SSR Lambda — and this route is the one
// that pays most for it: the render is ~45 ms, but the un-cached CF→us-west-2
// round trip is 250–550 ms, plus ~1.2 s whenever the low-traffic Lambda is cold
// (VIT-17795).
//
// s-maxage=1209600 is 14 days, set to track upstream freshness rather than the
// page's apparent liveness: the Ribbon half (ratings, cost, educations,
// certifications) refreshes on a 30-day cycle (RIBBON_CACHE_MAX_AGE in
// apps/providers), so 14 days sits inside it. The NPPES half refreshes daily,
// which is the trade-off on record — a name, address or phone could in
// principle be up to 14 days stale. In practice every production deploy
// invalidates `/us*` (sst.config.ts), so the effective TTL is hours.
//
// max-age=1800 is 30 minutes in the browser, half of results.tsx's hour. It has
// to be non-zero for the provider-card prefetch to buy anything (VIT-17906):
// result cards render `prefetch="viewport"`, so the browser fetches this route's
// `.data` while the card is on screen — but under max-age=0 that copy is stale
// the moment it lands, and clicking through revalidates it over the network
// anyway. The prefetch then costs a request and saves nothing.
//
// The trade-off this reverses (VIT-17795): a returning viewer's own cache can
// now mask a deploy invalidation, but only for 30 minutes, and the page is
// derived purely from the NPI in the path.
//
// Set on the loader's success return only, so a thrown 404/500 never picks it
// up and stays uncached at the edge (see the `headers` export above).
const CACHE_CONTROL = 'public, max-age=1800, s-maxage=1209600, stale-while-revalidate=86400'

// A degraded 200 is a real page, but assembled off fewer sources than it should
// have been — the Ribbon block might be missing entirely. Holding that at the
// edge for 14 days would outlast the upstream failure that caused it by weeks,
// so the thin render gets a 5-minute TTL instead: long enough to still shield
// the origin from a hot page, short enough to pick the missing source back up
// once it recovers. No stale-while-revalidate, for the same reason.
//
// The browser still gets a minute, so a card prefetched a moment ago is usable
// on the click that follows — the gap there is seconds. Deliberately far short
// of the 30 minutes a healthy page gets, so a thin render is not held locally
// for anything like as long as the upstream failure that caused it.
const DEGRADED_CACHE_CONTROL = 'public, max-age=60, s-maxage=300'

export const isNonEmpty = (value: string | null | undefined): value is string => value != null && value.trim() !== ''

export const loader = async ({ params }: Route.LoaderArgs) => {
  // The segment is `npi-<npi>-<slug>`; only the NPI is read (see app/slug.ts for why
  // the NPI leads). A segment that isn't that shape — including the old
  // `<slug>--<npi>` order — is not a provider URL, so 404 before calling upstream.
  const npi = parseProviderNpi(params['id'])

  if (npi == null) {
    throw data('Not Found', { status: 404 })
  }

  // The fetch, the retries and the degraded-source reporting live in
  // `api/provider-detail.server.ts` (VIT-18196), because the Open Graph card renderer
  // resolves the same record and the two must not drift: a card assembled off a
  // second implementation could describe the page differently from the page.
  const result = await fetchProviderDetail(npi)

  if (result.kind === 'not-found') {
    throw data('Not Found', { status: 404 })
  }

  if (result.kind === 'failed') {
    // `handleError` (entry.server.tsx) serializes `cause` via pino's err
    // serializer, so the upstream detail also reaches Datadog on the
    // unhandled-error log entry this throw produces.
    throw new Error(result.message, { cause: result.cause })
  }

  const { provider } = result

  // The results page this provider belongs on, for the breadcrumb's middle rung
  // (VIT-18219): the provider's ZIP resolved to the dataset city/state, so the
  // rung URL's city/state/ZIP pairing is valid by construction — the results
  // loader 404s a mismatched one. Null when the address carries no usable ZIP
  // or the lookup doesn't resolve it (the rung then drops rather than linking
  // a 404 — a failed lookup degrades the same way). Read after the detail fetch
  // because the ZIP lives in its response.
  const postalCode = provider.address?.postalCode?.trim().slice(0, 5) ?? null
  const zipResolution = postalCode != null && /^\d{5}$/.test(postalCode) ? await resolveZipLocation(postalCode) : null
  const zipLocation =
    zipResolution?.status === 'resolved' && postalCode != null
      ? { city: zipResolution.city, state: zipResolution.state, postalCode }
      : null

  // `degraded` rides in the payload rather than being recomputed in `meta`: it decides
  // which card `og:image` points at, and a share preview must not describe a page
  // assembled off fewer sources than it should have been (VIT-17974).
  return data(
    { provider, zipLocation, degraded: result.degraded },
    { headers: { 'Cache-Control': result.degraded ? DEGRADED_CACHE_CONTROL : CACHE_CONTROL } },
  )
}

/**
 * The breadcrumb trail for a provider page: home → the results page this provider
 * would appear on → the provider.
 *
 * Three rungs when both halves of the middle one are derivable, two when they are
 * not. The middle rung needs a real URL — Google requires `item` on every rung but
 * the last — so it is emitted only when the provider's ZIP resolved to a dataset
 * city/state inside our coverage (giving a city/state/ZIP triple whose pairing is
 * valid by construction — the results loader 404s a mismatched one, VIT-18219)
 * AND its specialty maps to a slug in our curated registry. Neither is
 * guaranteed: the endpoint resolves `specialty` across the whole NUCC taxonomy,
 * so most providers' specialty has no results page to point at.
 *
 * There is deliberately no city-only rung: `/us/{location}` is in the URL grammar
 * (docs/url-structure.md) but no route serves it, so it would link a 404.
 */
const providerBreadcrumbs = (
  provider: ProviderDetail,
  zipLocation: { city: string; state: string; postalCode: string } | null,
): BreadcrumbRung[] => {
  const specialty = provider.specialty != null ? specialtyForDisplayName(provider.specialty) : undefined

  const resultsRung =
    zipLocation != null && isUsRegionCode(zipLocation.state) && specialty != null
      ? {
          // formatLocationLabel, not the raw city: the ZIP dataset mirrors HUD,
          // whose PREF_CITY is ALL-CAPS, so "PORTLAND" has to be title-cased
          // (VIT-17805). The ZIP is part of the rung name, matching the heading
          // the linked results page renders (VIT-18219).
          name: `${specialty.pluralLabel} in ${formatLocationLabel({ city: zipLocation.city, region: zipLocation.state })} ${zipLocation.postalCode}`,
          path: href('/us/:location/:specialty', {
            location: formatCityZipSlug(zipLocation.city, zipLocation.state, zipLocation.postalCode),
            specialty: specialty.slug,
          }),
        }
      : null

  return [{ name: SITE_NAME, path: href('/') }, ...(resultsRung != null ? [resultsRung] : []), { name: provider.name }]
}

/**
 * What the provider's share card shows, as a sentence — the `og:image:alt` for a reader
 * whose client does not render the image, or who does not see it at all.
 *
 * Describes the CARD, not the page: the card carries the name, the credentials, the
 * specialty chip and the practice location, so those are what it says. The rating and cost
 * are deliberately left out — they are on the card when the record has them, but restating
 * numbers here would make the alt text disagree with the image for every provider that
 * has neither, and an alt that over-promises is worse than a shorter true one.
 *
 * Falls back through the same absences the card does: the name is the only guaranteed
 * part.
 */
const providerCardAlt = (provider: ProviderDetail): string => {
  const named = [provider.name, provider.degreeSuffix]
    .filter(isNonEmpty)
    .map((value) => value.trim())
    .join(', ')
  const place = [provider.address?.city, provider.address?.state]
    .filter(isNonEmpty)
    .map((value) => value.trim())
    .join(', ')
  const specialty = isNonEmpty(provider.specialty) ? provider.specialty.trim() : null

  const described = [specialty, place === '' ? null : `in ${place}`].filter((part) => part != null).join(' ')

  return described === '' ? `${named} on ${SITE_NAME}` : `${named} — ${described}, on ${SITE_NAME}`
}

export const meta = ({ loaderData }: Route.MetaArgs): Route.MetaDescriptors => {
  // `loaderData` is undefined when the loader threw (404/error) — fall back to
  // generic wording so the tab still reads sensibly on the error boundary. The
  // endpoint guarantees a non-empty name (falls back to the NPI).
  const provider = loaderData?.provider

  // The middle-initial form, not `provider.name` (VIT-18030): the location below is
  // not enough on its own, because 18+ providers share a name AND a practice — two
  // "Robert Gilson" dermatologists at one San Antonio address, whose titles were
  // identical down to the city. "Robert C." / "Robert T." separates them.
  //
  // Titles and descriptions only. The heading, the breadcrumb trail and the
  // canonical slug all stay on `provider.name` — the slug especially, since
  // changing it would move every provider URL.
  const name = provider?.nameWithMiddleInitial

  // "Jane C. Doe, MD, in Austin, TX". Names are not unique across providers, so the
  // name alone gave same-named providers the same title — which SEMrush reports as
  // duplicate titles. The practice location is what tells the rest of them apart.
  //
  // The post-nominals join it in VIT-18212. They are the first thing a reader looks
  // for in a doctor's name — "MD" is what separates the physician from the practice
  // manager of the same name — and the page, the card and the description all
  // already carry them, so the title was the one surface naming the provider
  // without them. `formatProviderNameLead` also closes the comma pair the
  // post-nominals open, so this reads as a sentence rather than "Jane C. Doe, MD in
  // Austin, TX".
  //
  // `isNonEmpty` so a blank city or state counts as absent: a half-known address
  // then reads "in Austin" rather than trailing a stray comma, and an address
  // with neither falls back to the bare name rather than to "in ".
  //
  // Trimmed as well as tested, because `isNonEmpty` only trims to decide — a
  // padded upstream value would otherwise reach the tab with its whitespace.
  const cityState = [provider?.address?.city, provider?.address?.state]
    .filter(isNonEmpty)
    .map((value) => value.trim())
    .join(', ')
  //
  // `''` counts as no name at all, which is what makes the `fittedTitle` fallback
  // below reachable: `formatProviderName` returns the empty string for a name that
  // is missing or whitespace-only, and a title of " | Vital Care Finder" names
  // nobody.
  const named = formatProviderName(name, provider?.degreeSuffix)
  const nameLead = formatProviderNameLead(name, provider?.degreeSuffix, cityState !== '')
  const located = named === '' ? undefined : cityState === '' ? named : `${nameLead} in ${cityState}`

  // The slug is decorative and any spelling of it is served rather than redirected
  // (VIT-17797), so every variant would otherwise compete as duplicate content.
  // Point them all at the canonical spelling. Built from the NPI the endpoint
  // resolved rather than the one parsed out of the URL, and carrying no query
  // string, so whatever params happen to be on the served URL — a shared link's
  // `utm_*`, say — never enter the canonical. The card link on results.tsx is
  // already path-only for its own reasons, so it is not a source of any.
  const canonicalPath =
    provider == null ? undefined : href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) })

  // The one title on the site built from data we don't control the length of, so the
  // only one that can overrun the 70-char budget SEMrush and Ahrefs measure against
  // (VIT-18071). It overruns on organisations rather than on people: `name` falls
  // back to the NPPES `organizationName` for records with no personal name, and
  // "San Luis Internal Medicine Associates, A Medical Corporation" spends 60 of the
  // 70 before the location or the suffix is added. Every individual provider in a
  // sampled city fitted; two thirds of the organisations there did not.
  //
  // The brand suffix goes before the location, which is the opposite of the obvious
  // order, for three reasons:
  //
  //  - The location is what people search ("internist san luis obispo"); the site
  //    name is not a query anyone types for a provider page.
  //  - The location is load-bearing for uniqueness. VIT-18030 put it there to break
  //    the duplicate titles SEMrush reported, so shedding it re-opens the defect
  //    this ticket's parent exists to close.
  //  - The brand is not actually lost: `og:site_name` still carries it, and
  //    pageMeta already strips this suffix out of `og:title` on every other page.
  //
  // Worth knowing what this does NOT do: shorten the name. Trimming corporate
  // boilerplate ("A Medical Corporation", ", Inc.") would fit more titles whole, but
  // it belongs upstream in apps/providers where the h1, the slug and the JSON-LD
  // would move with it — not in a title-only patch that would leave them disagreeing.
  //
  // The ladder gained a rung in VIT-18212: brand suffix, then location, then the
  // post-nominals, then the bare name. The credentials are shed second-to-last
  // rather than with the location because they cost 4–8 characters against a
  // 70-character budget while the location costs 15–25 — so on the organisation
  // names that actually overrun, dropping the location is what buys the room, and
  // an organisation has no post-nominals to shed anyway.
  const fittedTitle =
    located == null
      ? 'Provider | Vital Care Finder'
      : fitTitle(`${located}${TITLE_SUFFIX}`, located, named, name?.trim() ?? named)

  const page = {
    title: fittedTitle,
    // Built from the provider's own rating, focus tags, cost band, certification
    // and school rather than from a fixed sentence naming the categories those
    // fields belong to (VIT-18206). Still carries the NPI, which is what keeps it
    // unique per page where the title cannot be (VIT-18030). See
    // provider-description.ts for the clause order and what the budget sheds.
    description: provider != null ? providerDescription(provider) : GENERIC_PROVIDER_DESCRIPTION,
    canonicalPath,
  }

  // This provider's own share card, served from the page's path under `/og`
  // (og/card-path.ts). Built from `canonicalPath`, so every slug spelling of the page
  // points at ONE card URL — otherwise a decorative slug variant would be its own cache
  // key and its own render for an identical image.
  //
  // Omitted in two cases, both falling back to `pageMeta`'s default (the static
  // site-wide card):
  //
  //  - The loader threw, so there is no record to build a card from — and a 404 should
  //    not advertise a preview of a page that did not load.
  //  - The read was degraded. The renderer 404s for a degraded record rather than
  //    rendering a thinner card (see og/handler.server.ts), so pointing at it would
  //    leave the page with no image at all; the static card is the honest fallback, and
  //    it is the same rule the JSON-LD follows (VIT-17746).
  const card =
    provider == null || canonicalPath == null || loaderData?.degraded === true
      ? null
      : { image: cardPath(canonicalPath), imageAlt: providerCardAlt(provider) }

  return [
    ...pageMeta(card == null ? page : { ...page, ...card }),

    ...(provider == null || canonicalPath == null
      ? []
      : [
          { 'script:ld+json': breadcrumbList(providerBreadcrumbs(provider, loaderData?.zipLocation ?? null)) },
          // Only fields the page renders, each guarded on real loader data — a
          // degraded read (`provider.sources` reporting `error`) emits a smaller
          // node rather than one with blanks. See structured-data.ts.
          { 'script:ld+json': physician(provider, canonicalPath) },
        ]),
  ]
}

// The label for a location's card: its own facility name, falling back to the
// provider's organisation name. Whitespace-only counts as absent, not as a name
// — `renderableLocations` keeps a location whose facility name is blank but
// whose address is real, and `??` alone would then paint an empty heading and
// label the call link "Call    on (212) 555-0142".
const facilityLabel = (location: ProviderLocation, organizationName: string | null): string | null =>
  [location.facilityName, organizationName].map((value) => value?.trim()).find(isNonEmpty) ?? null

// The location's own number, formatted for display, or null. `phones[0]` of THIS
// location, never the top-level `provider.phone` alias — every field of a
// location comes from one source record, which is what stops an address being
// paired with a number it did not come from. `formatPhoneNumber` only returns
// null for null/empty input, which `primaryPhone` already excludes, so the
// `?? raw` keeps the type honest.
const displayPhoneFor = (location: ProviderLocation): string | null => {
  const raw = primaryPhone(location)
  return raw != null ? (formatPhoneNumber(raw, false) ?? raw) : null
}

type ProviderLocationsProps = {
  locations: ProviderLocation[]
  organizationName: string | null
}

/**
 * A scrollable list of practice locations — each row just its facility name and
 * address — above a detail card that reflects the selected one: its facility,
 * address, whether it is taking patients, and its OWN phone number (VIT-18209).
 *
 * Selecting a row swaps the card below, phone included. That is the point: a
 * provider practises at several addresses but the office has one number per
 * place, so the number has to travel with the address the visitor picked rather
 * than sit under a heading that could pair it with the wrong one.
 *
 * A single location skips the list and shows just the card — that is the contact
 * card the page has always shown, and a one-row list would be noise. Maps come
 * later (see the section comment below).
 */
const ProviderLocations = ({ locations, organizationName }: ProviderLocationsProps) => {
  // Default to the first location — the payload's highest-confidence entry, and
  // the one the SSR/no-JS pass shows before any click can change it.
  const [selectedIndex, setSelectedIndex] = useState(0)
  const selected = locations[selectedIndex] ?? locations[0]
  // The caller only renders this for a non-empty list, but the index access is
  // typed as possibly-undefined — bail rather than paint a card with no place.
  if (selected == null) return null
  const multiple = locations.length > 1

  const selectedFacility = facilityLabel(selected, organizationName)
  const selectedAddress = formatAddressLine(selected.address)
  const selectedPhone = displayPhoneFor(selected)

  return (
    <div>
      {multiple && (
        <>
          {locations.length > 2 && (
            <h2 className="text-secondary-600 text-sm leading-tight mb-1 -mt-2">{locations.length} locations</h2>
          )}
          {/* Scrollable, capped height: a long roster never pushes the body
              content below the fold, and every row stays in the SSR'd markup —
              so a crawler and a no-JS visitor still get every facility and
              address. Hydrated clients keep the rows place-first; a separate
              no-JS-only fallback below unselected rows carries the numbers the
              detail card would otherwise hide behind a click. */}
          <ul className="grid grid-cols-1 gap-1 max-h-60 overflow-y-auto pr-1 pb-2" data-testid="provider-locations">
            {locations.map((location, index) => {
              const facility = facilityLabel(location, organizationName)
              const addressLine = formatAddressLine(location.address)
              const rowPhone = primaryPhone(location)
              // Most rows stay place-first. If a location is known only by its
              // phone, let that number stand in so the button is not icon-only.
              const rowDisplayPhone = displayPhoneFor(location)
              const showRowPhone = facility == null && addressLine === '' && rowDisplayPhone != null
              const isSelected = index === selectedIndex
              const showNoJsRowPhone = !isSelected && !showRowPhone && rowDisplayPhone != null

              return (
                <li key={locationKey(location)}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedIndex(index)
                      e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
                    }}
                    aria-label={showRowPhone ? `Phone number: ${rowPhone}` : undefined}
                    aria-pressed={isSelected}
                    data-testid="provider-location"
                    className={`w-full flex items-start gap-2 cursor-pointer rounded-xl border px-4 py-2 text-left transition-colors ${
                      isSelected
                        ? 'border-primary-300 bg-primary-50 shadow-sm'
                        : 'border-transparent hover:border-secondary-200'
                    }`}
                  >
                    <MapPin
                      aria-hidden="true"
                      className={`size-4 mt-0.5 ${isSelected ? 'fill-primary-500 [&_circle]:fill-white text-transparent' : 'fill-secondary-500 [&_circle]:fill-white text-transparent'}`}
                    />
                    <div>
                      {facility != null && (
                        <span className="block text-secondary-600 text-base leading-tight">{facility}</span>
                      )}
                      {addressLine !== '' && <p className="text-sm text-secondary-800">{addressLine}</p>}
                      {showRowPhone && <p className="text-sm text-secondary-800">{rowDisplayPhone}</p>}
                    </div>
                  </button>
                  {!showRowPhone && rowPhone != null && (
                    <>
                      <span aria-label={`Phone number: ${rowPhone}`} className="sr-only">
                        {rowPhone}
                      </span>
                      {showNoJsRowPhone && (
                        // SSR/no-JS visitors can never change the selected card, so keep
                        // each unselected office number visible in the raw HTML. root.tsx
                        // marks JS-capable documents before CSS applies, and app.css hides
                        // this fallback for hydrated clients.
                        <p className="js-disabled-only px-4 pt-1 text-sm text-secondary-800">{rowDisplayPhone}</p>
                      )}
                    </>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}

      {/* Detail card — the selected location's facility, address, taking-patients
          badge, and its own phone. Also the whole contact block when there is
          only one location. */}
      <div
        className={`bg-secondary-50 p-4 space-y-3 ${multiple ? 'rounded-b-2xl border-t' : 'rounded-2xl'}`}
        data-testid="provider-location-detail"
      >
        <div className="flex items-center gap-3" data-testid="provider-contact-summary">
          <div className="size-10 bg-primary-50 shrink-0 rounded-full grid place-items-center">
            <Hospital aria-hidden="true" className="size-5 text-primary-500" />
          </div>
          <div className="flex-1">
            {selectedFacility != null && (
              <h3 className="text-secondary-900 text-xl leading-tight">{selectedFacility}</h3>
            )}
            {selectedAddress !== '' && <p className="text-sm text-primary-800">{selectedAddress}</p>}
            {selected.acceptingNewPatients === true && (
              <p
                className="mt-1 inline-block rounded-full bg-primary-50 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-primary-700"
                data-testid="provider-location-accepting"
              >
                Accepting new patients
              </p>
            )}
          </div>
        </div>
        {selectedPhone != null && (
          <a
            href={formatTelHref(selectedPhone)}
            className="btn btn-primary btn-lg w-full sm:w-auto break-words"
            data-testid="provider-phone"
            // The number alone doesn't say which place it reaches — the label
            // names the place and still contains the visible number, so it
            // satisfies Label in Name (WCAG 2.5.3) rather than replacing it.
            aria-label={
              selectedFacility != null ? `Call ${selectedFacility} on ${selectedPhone}` : `Call ${selectedPhone}`
            }
          >
            <Phone aria-hidden="true" className="size-5 shrink-0 text-primary-600 stroke-0 fill-white" />
            {selectedPhone}
          </a>
        )}
      </div>
    </div>
  )
}

// Where the "back" affordance points. `to: -1` pops history — only possible
// once JS has confirmed there's an in-app entry to pop, so it renders as a
// button. A string is a concrete path and renders as a real <a>, so it works
// even if JS never runs.
type BackTarget = { label: string; to: number | string }

// No-JS default: a plain link home. Safe for SSR and for a visitor whose JS
// hasn't (yet, or ever) run — never a dead button. The effect below upgrades
// it once it can confirm a better destination exists.
const HOME_BACK_TARGET: BackTarget = { label: '← New search', to: href('/') }

const Provider = () => {
  const { provider } = useLoaderData<typeof loader>()
  const navigate = useNavigate()

  const [backTarget, setBackTarget] = useState<BackTarget>(HOME_BACK_TARGET)

  useEffect(() => {
    // React Router tracks the current entry's position in its history stack as
    // `idx`; anything above 0 means there is an in-app page to pop back to, so
    // navigate(-1) lands on the real results page the visitor came from.
    const idx: unknown = window.history.state?.idx
    if (typeof idx === 'number' && idx > 0) {
      setBackTarget({ label: '← Results', to: -1 })
      return
    }
    // Deep-linked entry (shared link, search result, new tab): there's no
    // history to pop. Recover a results destination from the referrer when the
    // visitor came from one of our own search pages; otherwise the default
    // home link already rendered is the right call.
    const resultsPath = resultsPathFromReferrer(document.referrer, window.location.origin)
    if (resultsPath != null) {
      setBackTarget({ label: '← Results', to: resultsPath })
    }
  }, [])

  const {
    name,
    degreeSuffix,
    specialty,
    headshotUrl,
    organizationName,
    isPcp,
    ratingsAvg,
    ratingsCount,
    efficiencyIndex,
    focusTags,
    educations,
    certifications,
  } = provider

  // Every place this provider practises, not just the first (VIT-18011). The
  // page used to read the flat `address` / `phone` / `facilityName` aliases,
  // which the endpoint lifts off `locations[0]` — so a provider with six
  // practices showed one and gave no sign the others existed. Those aliases are
  // still what `meta` and the breadcrumbs use: one location has to stand for the
  // page in a title, and `locations[0]` is the endpoint's own pick.
  //
  // Order is the payload's (confidence-descending); `renderableLocations` only
  // removes repeats and entries with nothing to show. See provider-locations.ts.
  const locations = renderableLocations(provider.locations)

  // The hero credential badge shows the first named certification; the full list
  // still renders in the body section below.
  const primaryCertification = certifications[0] ?? null

  // The back affordance rides in the SiteHeader's actions slot (a number pops
  // history, a string is a reconstructed results link, home otherwise — see the
  // backTarget effect above). Rendered here so the same smart destination is used
  // whether it's a live button or a no-JS link.
  const backAction =
    typeof backTarget.to === 'number' ? (
      <button
        type="button"
        onClick={() => navigate(backTarget.to as number)}
        className="btn btn-accent text-sm no-underline sm:text-base"
        data-testid="provider-back"
      >
        {backTarget.label}
      </button>
    ) : (
      <a href={backTarget.to} className="btn btn-accent text-sm no-underline sm:text-base" data-testid="provider-back">
        {backTarget.label}
      </a>
    )

  return (
    <>
      <SiteHeader actions={backAction} />
      <main className="grow-1">
        <div className="border-b border-secondary-100 pb-4 xs:pb-10 pt-2 px-3 sm:px-4">
          <div className="mx-auto w-full max-w-7xl">
            {(specialty != null || isPcp === true) && (
              <div className="flex flex-wrap items-center gap-4 xs:gap-6 mb-6">
                {specialty != null && (
                  <span
                    className="rounded-full bg-purple-50 px-4 py-1.5 text-sm border border-transparent font-bold text-purple-700 uppercase tracking-wide"
                    data-testid="provider-specialty"
                  >
                    {specialty}
                  </span>
                )}
                {isPcp === true && (
                  <span className="rounded-full bg-secondary-100 px-4 py-1.5 text-sm border border-transparent font-bold text-secondary-700 uppercase tracking-wide">
                    Primary care
                  </span>
                )}
              </div>
            )}

            <section className="grid grid-cols-1 gap-12">
              <div className="space-y-4 xs:space-y-6">
                <div className="flex items-stretch gap-4">
                  {/* No placeholder headshot here — an empty avatar is dead space
                      beside the name, so the heading takes the full row (VIT-18153). */}
                  <Avatar
                    name={name}
                    credentials={degreeSuffix}
                    url={headshotUrl}
                    className="size-27"
                    hideWhenMissing
                  />
                  {/* flex-1 min-w-0 so this block owns the row when the avatar is
                      absent, and a long name wraps inside it rather than pushing wide. */}
                  <div className="flex min-w-0 flex-1 flex-col justify-between">
                    <h1
                      className="font-display text-2xl leading-tight font-semibold text-secondary-800 sm:text-4xl"
                      data-testid="provider-name"
                    >
                      {name}
                      {degreeSuffix != null && (
                        <span className="block text-lg font-sans font-normal text-secondary-600">{degreeSuffix}</span>
                      )}
                    </h1>
                    {(typeof ratingsAvg === 'number' || efficiencyIndex != null) && (
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        {typeof ratingsAvg === 'number' && (
                          // Labelled for the same reason as the results card
                          // (VIT-17746): the star is decorative, so unlabelled this
                          // reads as "4.5 120 reviews". `role="img"` is what makes
                          // the label apply — see provider-card.tsx.
                          <div
                            role="img"
                            className="flex items-center gap-2"
                            data-testid="provider-rating"
                            aria-label={
                              ratingsCount != null
                                ? `Rated ${ratingsAvg.toFixed(1)} out of 5 from ${ratingsCount} ${pluralize(ratingsCount, 'review')}`
                                : `Rated ${ratingsAvg.toFixed(1)} out of 5`
                            }
                          >
                            <Star aria-hidden="true" className="size-5 stroke-none fill-primary-400" />
                            <span className="text-sm font-semibold text-secondary-800">{ratingsAvg.toFixed(1)}</span>
                            {ratingsCount != null && (
                              <span className="text-sm text-secondary-500">
                                ({ratingsCount} {pluralize(ratingsCount, 'review')})
                              </span>
                            )}
                          </div>
                        )}
                        {efficiencyIndex != null && (
                          <div className="flex items-center gap-1.5" data-testid="provider-cost">
                            <span className="text-sm font-semibold text-secondary-800">Cost:</span>
                            <DollarRating
                              efficiencyIndex={efficiencyIndex}
                              className="text-sm font-semibold leading-none"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {locations.length > 0 && (
                  <div className="lg:w-5/8 border-t pt-4 xs:pt-6 space-y-4">
                    <ProviderLocations locations={locations} organizationName={organizationName} />
                  </div>
                )}
              </div>

              {/* Maps will appear here - need to add grid-cols-[15fr_9fr] to the parent section and add the maps component here */}
            </section>
          </div>
        </div>

        <div className="px-3 sm:px-4">
          <div className="mx-auto w-full max-w-7xl py-4 sm:py-8 " data-testid="provider-page">
            {/* Body: main column + sidebar. Single column on mobile, 3-up from lg. */}
            <div className="grid grid-cols-1 gap-8  lg:grid-cols-[15fr_9fr]">
              <div className="space-y-6 sm:space-y-10 sm:*:pb-10 sm:divide-y sm:divide-secondary-100">
                {educations.length > 0 && (
                  <section className="space-y-4">
                    <h2 className="text-secondary-700">Education & Credentials</h2>
                    <ul className="space-y-3 pl-8">
                      {educations.map((edu, index) => {
                        const year = edu.year != null ? String(edu.year) : null
                        const detail = [edu.type, year].filter(isNonEmpty).join(' · ')
                        return (
                          <li
                            // Education entries have no stable id; index within the
                            // loader-provided array is the only key available.
                            // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on Ribbon education records
                            key={index}
                            className="relative before:block before:content-[''] before:size-4 before:bg-primary-300 before:rounded-full before:absolute before:-left-8 before:top-1/2 before:-translate-y-1/2"
                          >
                            <div className="font-semibold text-secondary-800 uppercase text-sm sm:text-base">
                              {edu.name}
                            </div>
                            {detail !== '' && <div className="text-sm text-secondary-700">{detail}</div>}
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                )}

                {certifications.length > 0 && (
                  <section className="space-y-4">
                    <h2 className="text-secondary-700">Board certifications</h2>
                    <ul className="divide-y divide-secondary-100">
                      {certifications.map((cert, index) => {
                        const detail = [cert.board, cert.startYear != null ? `Certified ${cert.startYear}` : null]
                          .filter(isNonEmpty)
                          .join(' · ')
                        return (
                          <li
                            // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on Ribbon certification records
                            key={index}
                            className="flex flex-col gap-0.5 py-3 first:pt-0 last:pb-0"
                          >
                            <span className="font-medium text-secondary-800">{cert.name}</span>
                            {detail !== '' && <span className="text-sm text-secondary-700">{detail}</span>}
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                )}
              </div>

              {/* At-a-glance sidebar: board certification summary + specialized
                treatments, mirroring the redesign's right-column pattern. The
                facility card now lives under the hero instead of here. */}
              {(primaryCertification != null || focusTags.length > 0) && (
                <aside className="row-start-1 lg:row-start-auto space-y-6">
                  {/* Board certification from the payload. Admitting privileges used to
                    sit alongside it as a hardcoded hospital — no source carries that
                    field, so the block is gone rather than invented. */}
                  {primaryCertification != null && (
                    <section className="flex gap-2" data-testid="provider-board-certification-summary">
                      <div className="grid place-items-center size-10 bg-primary-50 rounded-full shrink-0">
                        <Shield aria-hidden="true" className="size-5 text-primary-500 rounded-full" />
                      </div>
                      <div>
                        <div className="text-secondary-700 text-sm uppercase tracking-wider font-bold">
                          Board Certified
                        </div>
                        <p className="text-sm text-secondary-700" data-testid="provider-board-certification">
                          {primaryCertification.board ?? primaryCertification.name}
                        </p>
                      </div>
                    </section>
                  )}

                  {focusTags.length > 0 && (
                    <section className="space-y-4">
                      <h2 className="text-secondary-700">Specialized Treatments</h2>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1">
                        {focusTags.map((tag) => (
                          <div
                            key={tag}
                            className="rounded-2xl bg-secondary-50 border font-medium border-secondary-100 p-4 text-sm text-secondary-700"
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </aside>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // A thrown 404 Response means the path segment wasn't the `npi-<npi>-<slug>`
  // shape, or both provider sources reported not-found — the provider genuinely
  // isn't there,
  // so retrying won't help. Any other throw is a degraded/failed read of both
  // sources (bad status or network) and may clear on a retry. The upstream
  // detail stays out of the UI — it's logged for Datadog, not for visitors.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404

  // The route owns the SiteHeader (this boundary replaces the route component,
  // not a layout), so render it here too — without it the error state loses the
  // wordmark and Home navigation. No back action: there's no provider or results
  // context to return to from an error.
  return (
    <>
      <SiteHeader />
      {isNotFound ? (
        <StatusScreen
          variant="not-found"
          data-testid="provider-error"
          title="We couldn't find that provider"
          description="This doctor may have been removed, or the link may be out of date. Try searching again to find another provider."
        >
          <BackHomeLink />
        </StatusScreen>
      ) : (
        <StatusScreen
          variant="error"
          data-testid="provider-error"
          title="Something went wrong"
          description="We couldn't load this provider just now — it's on us, not you. Please try again in a moment."
        >
          <TryAgainButton />
          <BackHomeLink />
        </StatusScreen>
      )}
    </>
  )
}

export default Provider
